import i18n from "intl/i18n"
import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink } from "@/types"
import { Purpose, ApiLinkEntity, ApiLinkRel, Currency } from "config/constants"
import Location, { ILocationDto } from "./Location"
import Address, { IAddressDto } from "./Address"
import ReservationTime, { IReservationTimeDto } from "./ReservationTime"
import ReservationArticle, { IGetReservationArticleDto } from "./ReservationArticle"
import DowntimePeriod, { IGetDowntimePeriodDto } from "./DowntimePeriod"
import ReservationAsset from "./ReservationAsset"
import CustomerExtended, { ICustomerExtendedDto } from "./CustomerExtended"
import ReservationPricingDetails from "./ReservationPricingDetails"
import getCustomerBasedCurrency from "utils/getCustomerBasedCurrency"

export enum ReservationStatus {
    reserved = "reserved",
    confirmed = "confirmed",
    shipping = "shipping",
    active = "active",
    return = "return",
    invoice = "invoice",
    finalSettlement = "finalSettlement",
    completed = "completed",
    closed = "closed",
    canceled = "canceled"
}

export enum ReservationSortBy {
    article = "article",
    requestNo = "requestNo",
    purpose = "purpose",
    customer = "customer",
    startDate = "startDate",
    endDate = "endDate",
    status = "status",
}

export enum ReferenceNumbersUpdateType {
    noUpdates = "noUpdates",
    collective = "collective",
    specific = "specific",
}

export enum InvoiceIntervalRule {
    splitEndOfYear = "splitEndOfYear",
    firstDayOfMonth = "firstDayOfMonth",
    allAtOnce = "allAtOnce",
    noRule = "noRule",
}

export interface IReservationPricingSummaryItem {
    label: string
    value: number
}

export interface IGetReservationNumbersItemDto {
    number: string
    assets: Guid[]
}

export interface IRentToBuy {
    mark?: boolean | null
    relatedReservationId?: Guid | null
    changeAssetTypeOnRelease?: boolean | null
    netValue?: number | null
    interestPercentage?: number | null
}

export interface IReservationRentToBuy extends IRentToBuy {
    changeAssetTypeLastUpdateDate?: string | null
    changeAssetTypeLastUserId?: Guid | null
}


export interface IGetReservationReferenceNumbers {
    updateType?: ReferenceNumbersUpdateType | null
    numberSale?: string | null
    numberRental?: string | null
    costUnit?: string | null
    costUnitUserDefined: boolean
    useFirstAssetOfConfiguration: boolean
}

export interface IGetReservationDto {
    id: Guid
    number: string
    status: ReservationStatus
    time: IReservationTimeDto
    articleId: Guid
    assetId?: Guid | null
    articles?: IGetReservationArticleDto[]
    address: IAddressDto
    recipientName?: string | null
    constructionSite?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    purpose: Purpose
    language: string
    warehouseId?: Guid | null
    customer: ICustomerExtendedDto
    currency?: Currency | null
    location: ILocationDto
    configurationCompleted?: boolean
    comment?: string
    assetCount: number
    shippingDetails?: string
    readyForShipping: boolean
    salesPersonNumber?: string
    salesAdminNumber?: string
    crmReferenceNumber?: string
    customerOrderNumber?: string
    customerOrderDate: string
    deliveryTermId?: string
    workingDaysCalendarId?: Guid | null
    workingDaysPerMonth?: number | null
    invoiceIntervalRule?: InvoiceIntervalRule | null
    invoiceIntervalRuleCustom?: boolean | null
    downtimePeriods?: IGetDowntimePeriodDto[]
    targetCountryCode?: string
    cancellationReason?: string | null
    rentToBuy: IReservationRentToBuy
    commission?: number | null
    profitCenterId?: Guid | null
    shortages: boolean
    latestNotCanceledInvoiceEnd?: string | null
    hasInvoices: boolean
    hasNotCanceledInvoices: boolean
    hasInvoiceBeforeShipping: boolean
    disposalDate?: string | null
    invoicing: boolean
    automaticInvoicing?: boolean | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    referenceNumbers: IGetReservationReferenceNumbers
    created: string
    createdBy: Guid
    changed: string
    changedBy: Guid
    links?: IApiLink[]
}

export const RESERVATION_INVOICE_TEXT_LINE_BREAK_AFTER = 55

export default class Reservation {
    id: Guid
    number: string
    status: ReservationStatus
    time: ReservationTime
    articleId: Guid
    assetId?: Guid | null
    articles?: ReservationArticle[]
    address: Address
    recipientName?: string
    constructionSite?: string
    personInCharge?: string
    personInChargePhone?: string
    purpose: Purpose
    language: string
    warehouseId?: Guid
    customer: CustomerExtended
    currency?: Currency
    location: Location
    configurationCompleted?: boolean
    comment?: string
    assetCount: number
    shippingDetails?: string
    readyForShipping: boolean
    salesPersonNumber?: string
    salesAdminNumber?: string
    crmReferenceNumber?: string
    customerOrderNumber?: string
    customerOrderDate: Dayjs
    deliveryTermId?: string
    workingDaysCalendarId: Guid | null
    workingDaysPerMonth: number | null
    invoiceIntervalRule?: InvoiceIntervalRule
    invoiceIntervalRuleCustom: boolean
    downtimePeriods: DowntimePeriod[]
    targetCountryCode?: string
    cancellationReason: string | null
    rentToBuy: IReservationRentToBuy
    commission: number | null
    profitCenterId?: Guid
    shortages: boolean
    latestNotCanceledInvoiceEnd: Dayjs | null
    hasInvoices: boolean
    hasNotCanceledInvoices: boolean
    hasInvoiceBeforeShipping: boolean
    disposalDate?: Dayjs | null
    invoicing: boolean
    automaticInvoicing: boolean
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    referenceNumbers: IGetReservationReferenceNumbers
    created: Dayjs
    createdBy: Guid
    changed: Dayjs
    changedBy: Guid
    linkDelete?: IApiLink
    linkReopen?: IApiLink
    linkGetShippingOrders?: IApiLink
    linkGetReservationDocuments?: IApiLink
    linkAddReservationDocuments?: IApiLink
    linkGetPricingDetails?: IApiLink
    linkUpdateComment?: IApiLink

    pricingDetails?: ReservationPricingDetails

    constructor(dto: IGetReservationDto) {
        this.id = dto.id
        this.number = dto.number
        this.status = dto.status
        this.time = new ReservationTime(dto.time)
        this.articleId = dto.articleId
        this.assetId = dto.assetId
        this.articles = dto.articles?.map(reservationArticleDto => new ReservationArticle(reservationArticleDto)) ?? undefined
        this.address = new Address(dto.address)
        this.recipientName = dto.recipientName ?? undefined
        this.constructionSite = dto.constructionSite ?? undefined
        this.personInCharge = dto.personInCharge ?? undefined
        this.personInChargePhone = dto.personInChargePhone ?? undefined
        this.purpose = dto.purpose
        this.language = dto.language
        this.warehouseId = dto.warehouseId ?? undefined
        this.customer = new CustomerExtended(dto.customer)
        this.currency = dto.currency ?? undefined
        this.location = new Location(dto.location)
        this.configurationCompleted = dto.configurationCompleted
        this.comment = dto.comment
        this.assetCount = dto.assetCount
        this.shippingDetails = dto.shippingDetails ?? undefined
        this.readyForShipping = dto.readyForShipping
        this.salesPersonNumber = dto.salesPersonNumber
        this.salesAdminNumber = dto.salesAdminNumber
        this.crmReferenceNumber = dto.crmReferenceNumber
        this.customerOrderNumber = dto.customerOrderNumber
        this.customerOrderDate = dayjs(dto.customerOrderDate, "YYYY-MM-DD")
        this.deliveryTermId = dto.deliveryTermId
        this.workingDaysCalendarId = dto.workingDaysCalendarId ?? null
        this.workingDaysPerMonth = dto.workingDaysPerMonth ?? null
        this.invoiceIntervalRule = dto.invoiceIntervalRule || undefined
        this.invoiceIntervalRuleCustom = dto.invoiceIntervalRuleCustom || false
        this.downtimePeriods = dto.downtimePeriods?.map(downtimePeriodDto => new DowntimePeriod(downtimePeriodDto)) ?? []
        this.targetCountryCode = dto.targetCountryCode ?? undefined
        this.cancellationReason = dto.cancellationReason ?? null
        this.rentToBuy = dto.rentToBuy
        this.commission = dto.commission ?? null
        this.profitCenterId = dto.profitCenterId ?? undefined
        this.shortages = dto.shortages
        this.latestNotCanceledInvoiceEnd = dto.latestNotCanceledInvoiceEnd ? dayjs.utc(dto.latestNotCanceledInvoiceEnd) : null
        this.hasInvoices = dto.hasInvoices
        this.hasNotCanceledInvoices = dto.hasNotCanceledInvoices
        this.hasInvoiceBeforeShipping = dto.hasInvoiceBeforeShipping
        this.disposalDate = dto.disposalDate ? dayjs(dto.disposalDate, "YYYY-MM-DD") : null
        this.invoicing = dto.invoicing
        this.automaticInvoicing = dto.automaticInvoicing ?? false
        this.deliveryNoteHeaderText = dto.deliveryNoteHeaderText ?? undefined
        this.deliveryNoteFooterText = dto.deliveryNoteFooterText ?? undefined
        this.referenceNumbers = dto.referenceNumbers
        this.created = dayjs(dto.created)
        this.createdBy = dto.createdBy
        this.changed = dayjs(dto.changed)
        this.changedBy = dto.changedBy
        this.linkDelete = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservation && lnk.rel === ApiLinkRel.delete)
        this.linkReopen = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservationReopen && lnk.rel === ApiLinkRel.self)
        this.linkGetShippingOrders = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.shippingOrder && lnk.rel === ApiLinkRel.all)
        this.linkGetReservationDocuments = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservationDocument && lnk.rel === ApiLinkRel.all)
        this.linkAddReservationDocuments = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservationDocument && lnk.rel === ApiLinkRel.new)
        this.linkGetPricingDetails = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservationPricingDetails && lnk.rel === ApiLinkRel.self)
        this.linkUpdateComment = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.reservationComment && lnk.rel === ApiLinkRel.update)
    }

    get assets(): ReservationAsset[] {
        const result: ReservationAsset[] = []
        return this.articles?.reduce((assets, article) => {
            assets.push(...article.assets.filter(a => !!a.assetId))
            return assets
        }, result) ?? result
    }

    get hasDeliveryTerms(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasSalesAdminField(): boolean {
        return this.purpose === Purpose.relocation
    }

    get hasSalesAndCustomerOrderInfo(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasPricing(): boolean {
        return this.purpose !== Purpose.relocation
    }

    get hasTargetCountryField(): boolean {
        return this.hasPricing
    }

    get hasInvoicingBySettings(): boolean {
        return this.hasPricing && this.purpose !== Purpose.sale
    }

    get hasInvoiceIntervalSettings(): boolean {
        return this.hasPricing && this.purpose !== Purpose.sale
    }

    printDate() {
        if (!this.time.reservationStart) return
        if (!this.time.reservationEnd) return this.time.reservationStart.format(i18n.t('config.dateFormat'))
        return `${this.time.reservationStart.format(i18n.t('config.dateFormat'))} – ${this.time.reservationEnd.format(i18n.t('config.dateFormat'))}`
    }

    static getDowntimePeriodsTotalDays(downtimePeriods: DowntimePeriod[]) {
        return downtimePeriods.reduce((total, downtimePeriod) => {
            // +1 is to include the start (or end) day
            return total + downtimePeriod.end.diff(downtimePeriod.start, "days") + 1
        }, 0)
    }

    getDowntimePeriodsDaysTotal() {
        return Reservation.getDowntimePeriodsTotalDays(this.downtimePeriods)
    }

    static formatReservationNumber(number: string = "") {
        const preparedNumber = number?.trim().replaceAll("-", "")
        if (!preparedNumber) return ""
        if (preparedNumber.length < 3) return preparedNumber
        return preparedNumber.slice(0, 2) + "-" + preparedNumber.slice(2);
    }

    static isReservationStatusSameOrAfter(currentStatus: ReservationStatus, statusToCheck: ReservationStatus) {
        if (currentStatus === statusToCheck) return true
        const statusArr = Object.keys(ReservationStatus)
        const currentStatusIndex = statusArr.indexOf(currentStatus)
        const statusToCheckIndex = statusArr.indexOf(statusToCheck)
        return currentStatusIndex >= statusToCheckIndex
    }

    get nodeAsset(): ReservationAsset | null {
        const nodeArticle = this.articles?.find(a => a.articleId === this.articleId)
        return nodeArticle?.assets[0] || null
    }

    get hasRentToBuySummarySection(): boolean {
        return this.purpose === Purpose.rent
            && (
                !!this.rentToBuy.mark
                || !!this.rentToBuy.relatedReservationId
                || !!this.rentToBuy.changeAssetTypeOnRelease
            )
    }

    get hasPricingRentToByFields(): boolean {
        return (this.purpose === Purpose.rent && !!this.rentToBuy.mark)
            || (this.purpose === Purpose.sale && !!this.rentToBuy.relatedReservationId)
    }

    /**
     * The effective request currency: the value persisted on the reservation, falling back to the customer-based
     * derivation for responses without the field.
     */
    get requestCurrency(): Currency | null {
        return this.currency ?? getCustomerBasedCurrency({
            hasCustomer: !!this.customer,
            customerCurrency: this.customer.currency,
        })
    }

    get isEarlyReturnPossible(): boolean {
        return this.purpose !== Purpose.sale
            && (this.status === ReservationStatus.active
                || this.status === ReservationStatus.return
                || this.status === ReservationStatus.finalSettlement
            )
    }
}

export const getReservationStatusLabel = (status: ReservationStatus): string => {
    return {
        [ReservationStatus.reserved]: i18n.t('reservation.status.reserved'),
        [ReservationStatus.confirmed]: i18n.t('reservation.status.confirmed'),
        [ReservationStatus.shipping]: i18n.t('reservation.status.shipping'),
        [ReservationStatus.active]: i18n.t('reservation.status.active'),
        [ReservationStatus.return]: i18n.t('reservation.status.return'),
        [ReservationStatus.finalSettlement]: i18n.t('reservation.status.finalSettlement'),
        [ReservationStatus.completed]: i18n.t('reservation.status.completed'),
        [ReservationStatus.invoice]: i18n.t('reservation.status.invoice'),
        [ReservationStatus.closed]: i18n.t('reservation.status.closed'),
        [ReservationStatus.canceled]: i18n.t('reservation.status.canceled'),
    }[status] || status
}
